/**
 * EditingView — S6.2 F1 在工作区内就地编辑文档节点.
 *
 * The third comparison view mode. On activation it materializes (freezes,
 * R1) the working tree and renders the reconstructed document as a list of
 * editable nodes. Decisions made in the diff views flow in as reconstruction
 * inputs, so this view reflects accept/reject state live; edits stack on top.
 *
 * Self-contained toolbar (lock banner + op count + undo) lives here rather
 * than in the shared TopBar so the editing session state stays in one hook.
 */
import { useMemo } from 'react';

import { veridocApi } from '../../lib/veridoc-api';
import type { RedlineSegment, VeriDocRole } from '../../lib/veridoc-api';

import { EditableNode } from './EditableNode';
import { InsertNodeControl, type InsertNodeType } from './InsertNodeControl';
import { useEditingState } from './useEditingState';

interface EditingViewProps {
  comparisonId: string;
  userRole: VeriDocRole | null;
  active: boolean;
  onError: (message: string) => void;
}

/** Pre-compute each segment's clean-children index (null for non-clean rows). */
function withCleanIndices(
  segments: RedlineSegment[],
): Array<{ segment: RedlineSegment; cleanIndex: number | null }> {
  let counter = 0;
  return segments.map((segment) => {
    if (segment.editable) {
      const cleanIndex = counter;
      counter += 1;
      return { segment, cleanIndex };
    }
    return { segment, cleanIndex: null };
  });
}

export function EditingView({
  comparisonId,
  userRole,
  active,
  onError,
}: EditingViewProps): React.JSX.Element {
  const canEdit = userRole === 'lawyer' || userRole === 'reviewer';
  const editing = useEditingState({
    comparisonId,
    enabled: active,
    canEdit,
    onError,
  });

  const rows = useMemo(
    () => withCleanIndices(editing.state?.segments ?? []),
    [editing.state],
  );

  const inapplicableCount = editing.state?.inapplicableOps.length ?? 0;

  return (
    <div className="flex flex-col gap-4">
      <Toolbar
        comparisonId={comparisonId}
        canEdit={canEdit}
        ready={editing.status === 'ready'}
        editOpCount={editing.state?.editOpCount ?? 0}
        canUndo={editing.canUndo}
        busy={editing.busy}
        onUndo={() => void editing.undo()}
      />

      {inapplicableCount > 0 && (
        <div className="rounded-md border border-semantic-warning bg-semantic-warningBg px-4 py-3">
          <p className="text-sm font-semibold text-semantic-warning">
            {inapplicableCount} 条编辑因决策变化暂不生效
          </p>
          <p className="mt-1 text-xs text-ink-700">
            这些编辑的目标节点已被当前 accept/reject 决策排除。改回相关决策后会重新生效，编辑未丢失。
          </p>
        </div>
      )}

      <Body
        status={editing.status}
        rows={rows}
        canEdit={canEdit}
        busy={editing.busy}
        onSetText={editing.setText}
        onDelete={editing.deleteNode}
        onInsert={editing.insertNode}
      />
    </div>
  );
}

function Toolbar({
  comparisonId,
  canEdit,
  ready,
  editOpCount,
  canUndo,
  busy,
  onUndo,
}: {
  comparisonId: string;
  canEdit: boolean;
  ready: boolean;
  editOpCount: number;
  canUndo: boolean;
  busy: boolean;
  onUndo: () => void;
}): React.JSX.Element {
  return (
    <div className="sticky top-0 z-10 -mx-2 flex items-center justify-between gap-4 border-b border-ink-200 bg-ink-0/95 px-2 py-2 backdrop-blur">
      <p className="text-xs text-ink-500">
        {ready && canEdit ? (
          <>
            <span className="font-medium text-ink-700">已进入定稿编辑</span>
            <span className="text-ink-300"> · </span>
            比对结构已锁定
          </>
        ) : ready ? (
          <span className="font-medium text-ink-700">定稿预览（只读）</span>
        ) : (
          '定稿编辑'
        )}
      </p>
      <div className="flex items-center gap-3">
        {canEdit && (
          <>
            <span className="text-xs text-ink-500">
              编辑 <span className="font-semibold text-ink-900">{editOpCount}</span> 步
            </span>
            <button
              type="button"
              onClick={onUndo}
              disabled={!canUndo || busy}
              title="撤销最近一次编辑"
              className="inline-flex h-btn-sm items-center gap-1 rounded-sm border border-ink-200 bg-ink-50 px-2 text-xs text-ink-600 hover:bg-ink-100 hover:text-ink-900 disabled:cursor-not-allowed disabled:opacity-40"
            >
              <span aria-hidden="true">↶</span>
              撤销
            </button>
          </>
        )}
        {ready && (
          <>
            <a
              data-testid="editing-export-redline"
              href={veridocApi.exportUrl(comparisonId, 'redline')}
              download
              title="导出可在 Word 中接受/拒绝修订的红线文档（Track Changes）"
              className="inline-flex h-btn-sm items-center gap-1 rounded-sm border border-brand-500 bg-brand-50 px-2 text-xs font-medium text-brand-700 hover:bg-brand-100"
            >
              <span aria-hidden="true">↓</span>
              导出红线
            </a>
            <a
              data-testid="editing-export-package"
              href={veridocApi.exportUrl(comparisonId, 'package')}
              download
              title="导出定稿交付包（干净稿 + 红线 + 决策清单 + 完整性标记）"
              className="inline-flex h-btn-sm items-center gap-1 rounded-sm border border-brand-600 bg-brand-600 px-2 text-xs font-medium text-white hover:bg-brand-700"
            >
              <span aria-hidden="true">⬇</span>
              导出定稿包
            </a>
          </>
        )}
      </div>
    </div>
  );
}

function Body({
  status,
  rows,
  canEdit,
  busy,
  onSetText,
  onDelete,
  onInsert,
}: {
  status: ReturnType<typeof useEditingState>['status'];
  rows: Array<{ segment: RedlineSegment; cleanIndex: number | null }>;
  canEdit: boolean;
  busy: boolean;
  onSetText: (nodeId: string, text: string) => void;
  onDelete: (nodeId: string) => void;
  onInsert: (index: number, text: string, type: string) => void;
}): React.JSX.Element {
  if (status === 'loading' || status === 'idle') {
    return <Centered text="正在进入定稿编辑…" />;
  }
  if (status === 'not-materialized') {
    return (
      <div className="rounded-md border border-semantic-info bg-semantic-infoBg px-4 py-3">
        <p className="text-sm font-semibold text-semantic-info">尚未进入定稿编辑</p>
        <p className="mt-1 text-xs text-ink-700">
          需要律师或审核员先进入定稿编辑，本次比对的定稿内容才可预览。
        </p>
      </div>
    );
  }
  if (status === 'error') {
    return <Centered text="进入定稿编辑失败，请稍后重试。" />;
  }
  if (rows.length === 0) {
    return <Centered text="无可编辑内容。" />;
  }

  return (
    <div className="flex flex-col">
      {canEdit && (
        <div className="mb-2">
          <InsertNodeControl
            busy={busy}
            label="在开头插入"
            onInsert={(text, type: InsertNodeType) => onInsert(0, text, type)}
          />
        </div>
      )}
      {rows.map(({ segment, cleanIndex }, i) => (
        <EditableNode
          // A modification slot emits two segments (delete v1 + insert v2)
          // sharing one nodeId, so the key folds in op + ordinal position.
          key={`${segment.nodeId}-${segment.op}-${i}`}
          segment={segment}
          cleanIndex={cleanIndex}
          canEdit={canEdit}
          busy={busy}
          onSetText={onSetText}
          onDelete={onDelete}
          onInsertBelow={(idx, text, type) => onInsert(idx + 1, text, type)}
        />
      ))}
    </div>
  );
}

function Centered({ text }: { text: string }): React.JSX.Element {
  return (
    <div
      role="status"
      aria-live="polite"
      className="flex min-h-[40vh] items-center justify-center text-sm text-ink-500"
    >
      {text}
    </div>
  );
}

export default EditingView;
